import type CategoryActionSource from "owa-categories/lib/utils/CategoryActionSource";
import { getCategoryMenuProps } from "owa-categories/lib/utils/getCategoryMenuProps";
import getMasterCategoryList from "owa-categories/lib/utils/getMasterCategoryList";
import type { IContextualMenuProps } from "@fluentui/react/lib/ContextualMenu";
import { DirectionalHint } from "@fluentui/react/lib/ContextualMenu";
import { isMonarchMultipleAccountsEnabled } from "owa-account-source-list";
import { lazyAddFavoriteCategory } from "owa-favorites";
import type { TableView } from "owa-mail-list-store";
import { getStore as getListViewStore, tryGetMailboxInfoFromRowKeysOrIdsIfUnifiedInboxOrDefault, } from "owa-mail-list-store";
import { getMailboxInfo } from "owa-mail-mailboxinfo";
import type { MailboxInfo } from "owa-client-types";
import type { ActionSource } from "owa-mail-store";
import { lazyAddCategoriesFromTable, lazyClearCategoriesFromTable, lazyRemoveCategoriesFromTable, lazyAddCategoriesFromItem, lazyClearCategoriesFromItem, lazyRemoveCategoriesFromItem, } from "owa-mail-triage-action";
import selectMailStoreItemById from "owa-mail-store/lib/selectors/selectMailStoreItemById";
import { getModuleContextMailboxInfo } from "owa-module-context-mailboxinfo";
import { lazyMountAndShowFullOptions } from "owa-options-view";
import { CATEGORY_MENU_WIDTH } from "./constants";
import { getAllCategoriesOnRows, getCommonCategoriesOnRows, } from "./getCategoryActionDecisionProperties";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { isSharedMailboxInfo } from "owa-client-types/lib/SharedMailboxInfo";
import { getMailCategorizeCallbacks } from "owa-calendar-unified-telemetry-utils/lib/utils/logMailCategorizeTelemetry";
import { type UTCalendarEventFeatureAction, type UTCalendarUIContext, type UTCalendarOrigin } from "@outlook/unified_telemetry";
import type CategoryType from "owa-service/lib/contract/CategoryType";
import executeCategorizeActionWithTelemetry from "./executeCategorizeActionWithTelemetry";

/**
 * Gets the category menu items for context menu
 * @param tableViewId the tableView id
 * @param dismissMenu the callback to be called to dismiss of menu
 * @param actionSource the actionSource
 * @param shouldShowSearch flag indicating whether to show search box or not
 * @param itemId the selected item
 * @param isItemPart flag indicating whether the context menu is for single item in conversation view
 * @return returns the contextual menu items
 */
export function getCategoryMenuPropertiesForContextMenu(tableViewId: string, dismissMenu: (ev?: any) => void, actionSource: ActionSource, shouldShowSearch?: boolean, itemId?: string, isItemPart?: boolean): IContextualMenuProps {
    const tableView = getListViewStore().tableViews.get(tableViewId);
    return isItemPart
        ? getCategoriesMenuItemProperties(getListViewStore().tableViews.get(tableViewId), "ContextLV", itemId, dismissMenu, DirectionalHint.rightTopEdge)
        : getCategoriesMenuProperties(tableView, shouldShowSearch ?? false /* showSearchBox */, dismissMenu, // Callback to dismiss the right click context menu
        actionSource, "ContextLV", DirectionalHint.rightTopEdge);
}

/**
 * Gets the category menu items
 * @param tableView the tableView
 * @param showSearchBox flag indicating whether to show search box or not
 * @param dismissMenu callback to be called to dismiss the category context menu
 * @param actionSource the actionSource
 * @param categoryActionSource the category action source
 * @param directionalHint determines which direction the callout appears, defaults to bottomLeftEdge
 * @return returns the contextual menu items
 */
function getCategoriesMenuProperties(tableView: TableView | undefined, showSearchBox: boolean, dismissMenu: () => void, actionSource: ActionSource, categoryActionSource: CategoryActionSource, directionalHint?: DirectionalHint): IContextualMenuProps {
    if (!tableView) {
        return { items: [] };
    }
    const rowKeys = () => [...tableView.selectedRowKeys.keys()];
    const mailboxInfo = tryGetMailboxInfoFromRowKeysOrIdsIfUnifiedInboxOrDefault(getMailboxInfo(tableView), rowKeys(), tableView);
    
    const categoryMenuProps = getCategoryMenuProps({
        actionSource: categoryActionSource,
        categoryList: getMasterCategoryList(mailboxInfo),
        mailboxInfo,
        getCategoriesToCheck: () => getCommonCategoriesOnRows(tableView, rowKeys()),
        dismissMenu: async (batchParams) => {
            const operations: Promise<void>[] = [];
            // batchParams is only populated by CategoryMenu when cal-multiSelectBatchUpdate is on.
            // Guard explicitly here so this handler never acts on batch data without the flight.
            if (batchParams && isGlobalFeatureEnabled("cal-multiSelectBatchUpdate")) {
                const { added, removed, finalCategories } = batchParams;
                if (finalCategories.length === 0) {
                    operations.push(executeCategorizeActionWithTelemetry("Clear", "MailList", "SurfaceContextMenu", mailboxInfo, onOperationError => lazyClearCategoriesFromTable.importAndExecute(rowKeys(), tableView, actionSource, onOperationError)));
                }
                else {
                    if (added.length > 0) {
                        operations.push(executeCategorizeActionWithTelemetry("SelectItem", "MailList", "SurfaceContextMenu", mailboxInfo, onOperationError => lazyAddCategoriesFromTable.importAndExecute(rowKeys(), tableView, added, actionSource, undefined /* createNewCategoryPromise */, onOperationError)));
                    }
                    if (removed.length > 0) {
                        operations.push(executeCategorizeActionWithTelemetry("UnselectItem", "MailList", "SurfaceContextMenu", mailboxInfo, onOperationError => lazyRemoveCategoriesFromTable.importAndExecute(rowKeys(), tableView, removed, actionSource, undefined /* isUndoAllowed */, onOperationError)));
                    }
                }
            }
            dismissMenu();
            await Promise.all(operations);
        },
        shouldShowSearchBox: showSearchBox,
        shouldShowManageCategories: !isSharedMailboxInfo(mailboxInfo),
        getShouldShowClearCategories: () => getAllCategoriesOnRows(tableView, rowKeys()).length > 0,
        addFavoriteCategory,
        onAfterCreateNewCategory: async (category: string, createNewCategoryPromise?: Promise<boolean>) => {
            await executeCategorizeActionWithTelemetry("New", "MailList", "SurfaceContextMenu", mailboxInfo, onOperationError => lazyAddCategoriesFromTable.importAndExecute(rowKeys(), tableView, [category], actionSource, createNewCategoryPromise, onOperationError));
        },
        onCheckedCategoryClicked: async (category) => {
            await executeCategorizeActionWithTelemetry("UnselectItem", "MailList", "SurfaceContextMenu", mailboxInfo, onOperationError => lazyRemoveCategoriesFromTable.importAndExecute(rowKeys(), tableView, [category], actionSource, undefined /* isUndoAllowed */, onOperationError));
        },
        onUncheckedCategoryClicked: async (category) => {
            await executeCategorizeActionWithTelemetry("SelectItem", "MailList", "SurfaceContextMenu", mailboxInfo, onOperationError => lazyAddCategoriesFromTable.importAndExecute(rowKeys(), tableView, [category], actionSource, undefined /* createNewCategoryPromise */, onOperationError));
        },
        onManageCategoryClicked: () => {
            const { onSuccess } = getMailCategorizeCallbacks("Manage", "MailList", "SurfaceContextMenu", mailboxInfo);
            onManageCategoryClicked(mailboxInfo);
            onSuccess();
        },
        onClearCategoriesClicked: () => {
            // NOTE: In batch mode (cal-multiSelectBatchUpdate), CategoryMenu never calls this —
            // the clear is committed atomically via the dismissMenu batchParams path instead.
            // This callback only fires in non-batch multi-select or non-multi-select mode.
            return executeCategorizeActionWithTelemetry("Clear", "MailList", "SurfaceContextMenu", mailboxInfo, onOperationError => lazyClearCategoriesFromTable.importAndExecute(rowKeys(), tableView, actionSource, onOperationError));
        },
        directionalHint,
        width: CATEGORY_MENU_WIDTH,
    });
    
    return categoryMenuProps;
}

/**
 * Gets the category menu items
 * @param tableView the tableView
 * @param categoryActionSource the category action source
 * @param itemId the selected item
 * @param dismissMenu the callback to be called to dismiss of menu
 * @param directionalHint determines which direction the callout appears, defaults to bottomLeftEdge
 * @return returns the contextual menu items
 */
function getCategoriesMenuItemProperties(tableView: TableView | undefined, categoryActionSource: CategoryActionSource, itemId: string | undefined, dismissMenu: () => void, directionalHint: DirectionalHint): IContextualMenuProps {
    if (!tableView || !itemId) {
        return { items: [] };
    }
    const item = selectMailStoreItemById(itemId);
    const mailboxInfo = item.MailboxInfo ?? getMailboxInfo(tableView);
    
    const categoryMenuProps = getCategoryMenuProps({
        actionSource: categoryActionSource,
        categoryList: getMasterCategoryList(mailboxInfo),
        mailboxInfo,
        getCategoriesToCheck: () => item.Categories ?? [],
        dismissMenu: async (batchParams) => {
            const operations: Promise<void>[] = [];
            // batchParams is only populated by CategoryMenu when cal-multiSelectBatchUpdate is on.
            // Guard explicitly here so this handler never acts on batch data without the flight.
            if (batchParams && isGlobalFeatureEnabled("cal-multiSelectBatchUpdate")) {
                const { added, removed, finalCategories } = batchParams;
                if (finalCategories.length === 0) {
                    operations.push(executeCategorizeActionWithTelemetry("Clear", "MailReadingPane", "SurfaceItemContextMenu", mailboxInfo, () => lazyClearCategoriesFromItem.importAndExecute(itemId, mailboxInfo)));
                }
                else {
                    if (added.length > 0) {
                        operations.push(executeCategorizeActionWithTelemetry("SelectItem", "MailReadingPane", "SurfaceItemContextMenu", mailboxInfo, () => lazyAddCategoriesFromItem.importAndExecute(itemId, mailboxInfo, added, undefined /* createNewCategoryPromise */, categoryActionSource)));
                    }
                    if (removed.length > 0) {
                        operations.push(executeCategorizeActionWithTelemetry("UnselectItem", "MailReadingPane", "SurfaceItemContextMenu", mailboxInfo, () => lazyRemoveCategoriesFromItem.importAndExecute(itemId, mailboxInfo, removed, categoryActionSource)));
                    }
                }
            }
            dismissMenu();
            await Promise.all(operations);
        },
        shouldShowSearchBox: true,
        shouldShowManageCategories: !isSharedMailboxInfo(mailboxInfo),
        getShouldShowClearCategories: () => false,
        addFavoriteCategory,
        onAfterCreateNewCategory: (category: string) => executeCategorizeActionWithTelemetry("New", "MailReadingPane", "SurfaceItemContextMenu", mailboxInfo, () => lazyAddCategoriesFromItem.importAndExecute(itemId, mailboxInfo, [category], undefined /* createNewCategoryPromise */, categoryActionSource)),
        onCheckedCategoryClicked: (category: string) => executeCategorizeActionWithTelemetry("UnselectItem", "MailReadingPane", "SurfaceItemContextMenu", mailboxInfo, () => lazyRemoveCategoriesFromItem.importAndExecute(itemId, mailboxInfo, [category], categoryActionSource)),
        onUncheckedCategoryClicked: (category: string) => executeCategorizeActionWithTelemetry("SelectItem", "MailReadingPane", "SurfaceItemContextMenu", mailboxInfo, () => lazyAddCategoriesFromItem.importAndExecute(itemId, mailboxInfo, [category], undefined /* createNewCategoryPromise */, categoryActionSource)),
        onManageCategoryClicked: () => {
            // Get telemetry callbacks for manage categories action (single item)
            const { onSuccess } = getMailCategorizeCallbacks("Manage", "MailReadingPane", "SurfaceItemContextMenu", mailboxInfo);
            onManageCategoryClicked(mailboxInfo);
            // Log successful telemetry
            onSuccess();
        },
        onClearCategoriesClicked: () => executeCategorizeActionWithTelemetry("Clear", "MailReadingPane", "SurfaceItemContextMenu", mailboxInfo, () => lazyClearCategoriesFromItem.importAndExecute(itemId, mailboxInfo)),
        directionalHint,
        width: CATEGORY_MENU_WIDTH,
    });
    
    return categoryMenuProps;
}

/**
 * Custom callback function when manage category is clicked
 */
function onManageCategoryClicked(mailboxInfo: MailboxInfo) {
    // Navigate to options/categories
    if (isMonarchMultipleAccountsEnabled() || isGlobalFeatureEnabled("acct-multiacctcomingsoon")) {
        lazyMountAndShowFullOptions.importAndExecute("accounts-category", "categories", undefined, undefined, mailboxInfo);
    }
    else {
        lazyMountAndShowFullOptions.importAndExecute("accounts-category", "categories");
    }
}

/**
 * Custom callback function when adding a favorite category
 */
function addFavoriteCategory(categoryId: string, actionSource: CategoryActionSource): void {
    lazyAddFavoriteCategory
        .import()
        .then(addFavoriteCategoryFunc => addFavoriteCategoryFunc(categoryId, actionSource, getModuleContextMailboxInfo()));
}

export function getCategoryMenuContextCallbacks(selectedRowKeys: string[], tableView: TableView, mailboxInfo: MailboxInfo, actionSource: ActionSource, isGroupHeaderSelected: boolean, // Used for group header actions, checked categories are not shown and the clear categories button always shown
getTargetRowKeysFunction?: () => Promise<string[]>): {
    getCategoriesToCheck: () => string[];
    getShouldShowClearCategories: () => boolean;
    onAfterCreateNewCategory: (category: string, createNewCategoryPromise?: Promise<boolean>) => Promise<void>;
    addFavoriteCategory: typeof addFavoriteCategory;
    onCheckedCategoryClicked: (category: string) => Promise<void>;
    onUncheckedCategoryClicked: (category: string) => Promise<void>;
    onBatchDismiss: (params: {
        added: string[];
        removed: string[];
        finalCategories: string[];
    }) => Promise<void>;
    categoryList: CategoryType[];
    onManageCategoryClicked: () => void;
    onClearCategoriesClicked: () => Promise<void>;
} {
    const callbacks = {
        getCategoriesToCheck: () => isGroupHeaderSelected ? [] : getCommonCategoriesOnRows(tableView, selectedRowKeys),
        getShouldShowClearCategories: () => isGroupHeaderSelected || getAllCategoriesOnRows(tableView, selectedRowKeys).length > 0,
        onAfterCreateNewCategory: async (category: string, createNewCategoryPromise?: Promise<boolean>) => {
            await executeCategorizeActionWithTelemetry("New", "MailList", "SurfaceContextMenu", mailboxInfo, async (onOperationError) => {
                const targetRowKeys = getTargetRowKeysFunction
                    ? await getTargetRowKeysFunction()
                    : selectedRowKeys;
                await lazyAddCategoriesFromTable.importAndExecute(targetRowKeys, tableView, [category], actionSource, createNewCategoryPromise, onOperationError);
            });
        },
        addFavoriteCategory,
        onCheckedCategoryClicked: async (category: string) => {
            await executeCategorizeActionWithTelemetry("UnselectItem", "MailList", "SurfaceContextMenu", mailboxInfo, async (onOperationError) => {
                const targetRowKeys = getTargetRowKeysFunction
                    ? await getTargetRowKeysFunction()
                    : selectedRowKeys;
                await lazyRemoveCategoriesFromTable.importAndExecute(targetRowKeys, tableView, [category], actionSource, undefined /* isUndoAllowed */, onOperationError);
            });
        },
        onUncheckedCategoryClicked: async (category: string) => {
            await executeCategorizeActionWithTelemetry("SelectItem", "MailList", "SurfaceContextMenu", mailboxInfo, async (onOperationError) => {
                const targetRowKeys = getTargetRowKeysFunction
                    ? await getTargetRowKeysFunction()
                    : selectedRowKeys;
                await lazyAddCategoriesFromTable.importAndExecute(targetRowKeys, tableView, [category], actionSource, undefined /* createNewCategoryPromise */, onOperationError);
            });
        },
        onBatchDismiss: async ({ added, removed, finalCategories, }: {
            added: string[];
            removed: string[];
            finalCategories: string[];
        }) => {
            const targetRowKeysPromise = getTargetRowKeysFunction
                ? getTargetRowKeysFunction()
                : Promise.resolve(selectedRowKeys);
            const operations: Promise<void>[] = [];
            if (finalCategories.length === 0) {
                operations.push(executeCategorizeActionWithTelemetry("Clear", "MailList", "SurfaceContextMenu", mailboxInfo, async (onOperationError) => {
                    const targetRowKeys = await targetRowKeysPromise;
                    await lazyClearCategoriesFromTable.importAndExecute(targetRowKeys, tableView, actionSource, onOperationError);
                }));
            }
            else {
                if (added.length > 0) {
                    operations.push(executeCategorizeActionWithTelemetry("SelectItem", "MailList", "SurfaceContextMenu", mailboxInfo, async (onOperationError) => {
                        const targetRowKeys = await targetRowKeysPromise;
                        await lazyAddCategoriesFromTable.importAndExecute(targetRowKeys, tableView, added, actionSource, undefined /* createNewCategoryPromise */, onOperationError);
                    }));
                }
                if (removed.length > 0) {
                    operations.push(executeCategorizeActionWithTelemetry("UnselectItem", "MailList", "SurfaceContextMenu", mailboxInfo, async (onOperationError) => {
                        const targetRowKeys = await targetRowKeysPromise;
                        await lazyRemoveCategoriesFromTable.importAndExecute(targetRowKeys, tableView, removed, actionSource, undefined /* isUndoAllowed */, onOperationError);
                    }));
                }
            }
            await Promise.all(operations);
        },
        categoryList: getMasterCategoryList(mailboxInfo),
        onManageCategoryClicked: () => onManageCategoryClicked(mailboxInfo),
        onClearCategoriesClicked: async () => {
            await executeCategorizeActionWithTelemetry("Clear", "MailList", "SurfaceContextMenu", mailboxInfo, async (onOperationError) => {
                const targetRowKeys = getTargetRowKeysFunction
                    ? await getTargetRowKeysFunction()
                    : selectedRowKeys;
                await lazyClearCategoriesFromTable.importAndExecute(targetRowKeys, tableView, actionSource, onOperationError);
            });
        },
    };
    return callbacks;
}

export function getCategoryPartItemMenuContextCallbacks(mailboxInfo: MailboxInfo, categoryActionSource: CategoryActionSource, itemId: string): {
    onAfterCreateNewCategory: (category: string, createNewCategoryPromise?: Promise<boolean>) => Promise<void>;
    addFavoriteCategory: typeof addFavoriteCategory;
    onCheckedCategoryClicked: (category: string) => Promise<void>;
    onUncheckedCategoryClicked: (category: string) => Promise<void>;
    onBatchDismiss: (params: {
        added: string[];
        removed: string[];
        finalCategories: string[];
    }) => Promise<void>;
    categoryList: CategoryType[];
    getCategoriesToCheck: () => string[];
    getShouldShowClearCategories: () => boolean;
    onManageCategoryClicked: () => void;
    onClearCategoriesClicked: () => Promise<void>;
} {
    const item = selectMailStoreItemById(itemId);
    mailboxInfo = item.MailboxInfo ?? mailboxInfo;
    
    const callbacks = {
        onAfterCreateNewCategory: (category: string) => executeCategorizeActionWithTelemetry("New", "MailReadingPane", "SurfaceItemContextMenu", mailboxInfo, () => lazyAddCategoriesFromItem.importAndExecute(itemId, mailboxInfo, [category], undefined /* createNewCategoryPromise */, categoryActionSource)),
        addFavoriteCategory,
        onCheckedCategoryClicked: (category: string) => executeCategorizeActionWithTelemetry("UnselectItem", "MailReadingPane", "SurfaceItemContextMenu", mailboxInfo, () => lazyRemoveCategoriesFromItem.importAndExecute(itemId, mailboxInfo, [category], categoryActionSource)),
        onUncheckedCategoryClicked: (category: string) => executeCategorizeActionWithTelemetry("SelectItem", "MailReadingPane", "SurfaceItemContextMenu", mailboxInfo, () => lazyAddCategoriesFromItem.importAndExecute(itemId, mailboxInfo, [category], undefined /* createNewCategoryPromise */, categoryActionSource)),
        onBatchDismiss: async ({ added, removed, finalCategories, }: {
            added: string[];
            removed: string[];
            finalCategories: string[];
        }) => {
            const operations: Promise<void>[] = [];
            if (finalCategories.length === 0) {
                operations.push(executeCategorizeActionWithTelemetry("Clear", "MailReadingPane", "SurfaceItemContextMenu", mailboxInfo, () => lazyClearCategoriesFromItem.importAndExecute(itemId, mailboxInfo)));
            }
            else {
                if (added.length > 0) {
                    operations.push(executeCategorizeActionWithTelemetry("SelectItem", "MailReadingPane", "SurfaceItemContextMenu", mailboxInfo, () => lazyAddCategoriesFromItem.importAndExecute(itemId, mailboxInfo, added, undefined /* createNewCategoryPromise */, categoryActionSource)));
                }
                if (removed.length > 0) {
                    operations.push(executeCategorizeActionWithTelemetry("UnselectItem", "MailReadingPane", "SurfaceItemContextMenu", mailboxInfo, () => lazyRemoveCategoriesFromItem.importAndExecute(itemId, mailboxInfo, removed, categoryActionSource)));
                }
            }
            await Promise.all(operations);
        },
        categoryList: getMasterCategoryList(mailboxInfo),
        getCategoriesToCheck: () => item.Categories ?? [],
        getShouldShowClearCategories: () => false,
        onManageCategoryClicked: () => {
            // Get telemetry callbacks for manage categories action (part item context)
            const { onSuccess } = getMailCategorizeCallbacks("Manage", "MailReadingPane", "SurfaceItemContextMenu", mailboxInfo);
            onManageCategoryClicked(mailboxInfo);
            // Log successful telemetry
            onSuccess();
        },
        onClearCategoriesClicked: () => executeCategorizeActionWithTelemetry("Clear", "MailReadingPane", "SurfaceItemContextMenu", mailboxInfo, () => lazyClearCategoriesFromItem.importAndExecute(itemId, mailboxInfo)),
    };
    return callbacks;
}
